import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Container, 
  Breadcrumbs, 
  Link, 
  Typography, 
  Tabs, 
  Tab, 
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
} from '@mui/material';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { styled } from '@mui/material/styles';
import { useNavbar } from '../context/NavbarContext';
import { NavigateNext } from '@mui/icons-material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import VisualizationTab from './VisualizationTab';
import HistoryLogs from './HistoryLogs';
import ForumTab from './ForumTab';
import TableTab from './TableTab';
import PlantView from '../components/PlantView';
import GatewayStatusBanner from '../components/GatewayStatusBanner';
import useGatewayStatus from '../hooks/useGatewayStatus';
import { 
  getSiteWithInstruments, 
  addGatewayToSite, 
  getTagNamesByProjectId,
  getUserFromDynamoDB
} from '../api/apiService';

// Disable console.log calls in this file
console.log = () => {};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '16px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? '0 8px 28px rgba(0,0,0,0.45)' : '0 6px 18px rgba(16,24,40,0.10)',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark' ? '0 12px 36px rgba(0,0,0,0.50)' : '0 10px 24px rgba(16,24,40,0.14)'
  }
}));



const IndividualProjectPage = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = location.state?.projectId; // Retrieve the projectId passed in the state object
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';
  const [, setLastUpdated] = useState(new Date());
  
  // Redirect Users/Engineers if they lack access to this project
  useEffect(() => {
    if ((userRole === 'User' || userRole === 'Engineer') && projectId && currentUser?.email) {
      const checkAccess = async () => {
        try {
          const userData = await getUserFromDynamoDB(currentUser.email);
          const allowedSites = userData?.sites || (userData?.site ? [userData.site] : []);
          if (!allowedSites.some(s => s.id === projectId)) {
            navigate('/dashboard');
          }
        } catch (err) {
          console.error('Error checking site access:', err);
          navigate('/dashboard');
        }
      };
      checkAccess();
    }
  }, [userRole, projectId, currentUser?.email, navigate]);

  // State to handle active tab
  const [selectedTab, setSelectedTab] = useState(0);
  
  // State to store project data and loading status
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [gatewayFormData, setGatewayFormData] = useState({ tag: '', serverName: '' });
  const [tagNames, setTagNames] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  // State for Plant View dialog
  const [openPlantView, setOpenPlantView] = useState(false);
  const [openForum, setOpenForum] = useState(false);
  const gatewayStatus = useGatewayStatus(projectId);

  const goToReportsAnalytics = () => {
    navigate('/reports-analytics', {
      state: projectId ? { projectId } : undefined,
    });
  };

  const refreshTagNames = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoadingTags(true);
      const tags = await getTagNamesByProjectId(projectId);
      setTagNames(tags);
    } catch (error) {
      console.error('Error fetching tag names:', error);
    } finally {
      setLoadingTags(false);
    }
  }, [projectId]);

  const refreshSiteData = useCallback(async () => {
    if (!projectId) return;

    try {
      const fullSiteData = await getSiteWithInstruments(projectId);
      setSiteData(fullSiteData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error refreshing site data:', error);
    }
  }, [projectId]);

  const refreshProjectInstruments = useCallback(async () => {
    await Promise.all([refreshSiteData(), refreshTagNames()]);
  }, [refreshSiteData, refreshTagNames]);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);  // Now returns the full item
        setSiteData(fullSiteData);  // Set to the full object
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Error fetching site data:', error);
        setSiteData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  useEffect(() => {
    refreshTagNames();
  }, [refreshTagNames]);

  useEffect(() => {
    if (selectedTab === 1) {
      refreshTagNames();
    }
  }, [selectedTab, refreshTagNames]);


  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };


  

  // Update the site info display
  const renderSiteInfo = () => {
    if (loading) {
      return (
        <StyledPaper elevation={0}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ width: '60%', height: 20, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1, mb: 1 }} />
              <Box sx={{ width: '40%', height: 16, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1 }} />
            </Box>
          </Box>
        </StyledPaper>
      );
    }

    if (!siteData) {
      return (
        <Typography 
          variant="h6" 
          sx={{ 
            color: (theme) => theme.palette.error.main, 
            opacity: 0.9, 
            fontWeight: '500', 
            width: '100%', 
            textAlign: 'center',
            padding: '16px',
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.12)' : 'rgba(211,47,47,0.08)',
            borderRadius: '8px',
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.32)' : 'rgba(211,47,47,0.24)'}`,
          }}
        >
          Project not found.
        </Typography>
      );
    }

    return (
        <StyledPaper elevation={0} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, pr: 2 }}>
        <Typography 
            variant="h5"
          sx={{ 
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 0.5,
              lineHeight: 1.3,
            fontSize: {
                xs: '1.1rem',
                sm: '1.2rem',
                md: '1.35rem'
            }
          }}
        >
            {siteData.id}: {siteData.client}, {siteData.location}
        </Typography>
        <Typography 
            variant="subtitle1"
          sx={{ 
              color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: {
                xs: '0.9rem',
                sm: '0.95rem',
                md: '1.05rem'
            }
          }}
        >
          {siteData.name}
        </Typography>
        </Box>
        {(userRole === 'Admin' || userRole === 'Manager') && siteData.gatewayTag && siteData.serverName && (
          <Box sx={{ textAlign: 'right', pt: { xs: 1, sm: 0 } }}>
            <Typography 
              variant="body2"
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary, 
                fontSize: { xs: '0.8rem', sm: '0.85rem' }
              }}
            >
              <Box component="span" sx={{ color: theme.palette.text.secondary }}>Gateway: </Box>{siteData.gatewayTag}
            </Typography>
            <Typography 
              variant="body2"
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary, 
                mt: 0.5, 
                fontSize: { xs: '0.8rem', sm: '0.85rem' }
              }}
            >
              <Box component="span" sx={{ color: theme.palette.text.secondary }}>Server: </Box>{siteData.serverName}
            </Typography>
            <GatewayStatusBanner {...gatewayStatus} />
          </Box>
        )}
      </StyledPaper>
    );
  };





  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: {
            xs: '12px',
            sm: '16px',
            md: '16px',
            lg: '20px',
            xl: '24px'
          },
          margin: 0,
          padding: 0,
          width: '100%',
          minHeight: '100%',
          overflow: 'visible',
        }}>
          {/* Header: Breadcrumbs and Actions */}
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 0 }}>
              <Breadcrumbs 
              separator={<NavigateNext fontSize="small" />} 
              aria-label="breadcrumb"
              sx={{
                '& .MuiBreadcrumbs-separator': {
                  color: theme.palette.text.secondary,
                },
                '& .MuiLink-root': {
                  color: theme.palette.text.secondary,
                  textDecoration: 'none',
                  fontSize: {
                    xs: '0.875rem',
                    sm: '0.9rem',
                    md: '1rem'
                  },
                  '&:hover': {
                    color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  }
                },
                '& .MuiTypography-root': {
                  color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  fontWeight: 500,
                  fontSize: {
                    xs: '0.875rem',
                    sm: '0.9rem',
                    md: '1rem'
                  }
                }
              }}
              >
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Link underline="hover" color="inherit" href="/individual-sites">
                  Sites
                </Link>
                <Typography color="text.primary">Individual Project</Typography>
              </Breadcrumbs>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                <Tooltip title="Reports & Analytics">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={goToReportsAnalytics}
                    sx={{ minWidth: 0, width: 40, height: 40, p: 0, borderRadius: '10px' }}
                  >
                    <AssessmentOutlinedIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="View Plant">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenPlantView(true)}
                    sx={{ minWidth: 0, width: 40, height: 40, p: 0, borderRadius: '10px',
                      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                      color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                      '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 } }}
                  >
                    <VisibilityOutlinedIcon />
                  </Button>
                </Tooltip>
                {(userRole === 'Admin' || userRole === 'Manager') && (
                  <Tooltip title="Add Gateway">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenGatewayDialog(true)}
                      sx={{ minWidth: 0, width: 40, height: 40, p: 0, borderRadius: '10px',
                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                        color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                        '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 } }}
                    >
                      <DevicesOutlinedIcon />
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Forum">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenForum(true)}
                    sx={{ minWidth: 0, width: 40, height: 40, p: 0, borderRadius: '10px',
                      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                      color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                      '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 } }}
                  >
                    <ForumOutlinedIcon />
                  </Button>
                </Tooltip>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 0 }}>
              <Breadcrumbs 
                separator={<NavigateNext fontSize="small" />} 
                aria-label="breadcrumb"
                sx={{
                  '& .MuiBreadcrumbs-separator': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiLink-root': {
                    color: theme.palette.text.secondary,
                    textDecoration: 'none',
                    fontSize: {
                      xs: '0.875rem',
                      sm: '0.9rem',
                      md: '1rem'
                    },
                    '&:hover': {
                    color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    }
                  },
                  '& .MuiTypography-root': {
                    color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    fontWeight: 500,
                    fontSize: {
                      xs: '0.875rem',
                      sm: '0.9rem',
                      md: '1rem'
                    }
                  }
                }}
              >
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Link underline="hover" color="inherit" href="/individual-sites">
                  Sites
                </Link>
                <Typography color="text.primary">Individual Project</Typography>
              </Breadcrumbs>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={goToReportsAnalytics}
                  startIcon={<AssessmentOutlinedIcon />}
                  sx={{
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                    color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                    '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 }
                  }}
                >
                  Reports & Analytics
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenPlantView(true)}
                  startIcon={<VisibilityOutlinedIcon />}
                  sx={{
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                    color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                    '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 }
                  }}
                >
                  View Plant
                </Button>
                {(userRole === 'Admin' || userRole === 'Manager') && (
                <Button variant="contained" color="primary" onClick={() => setOpenGatewayDialog(true)} startIcon={<DevicesOutlinedIcon />}
                  sx={{
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                    color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                    '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 }
                  }}
                >
                  Add Gateway
                </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenForum(true)}
                  startIcon={<ForumOutlinedIcon />}
                  sx={{
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.14)'),
                    color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'), boxShadow: 'none' },
                    '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`, outlineOffset: 2 }
                  }}
                >
                  Forum
                </Button>
              </Stack>
            </Box>
          )}

          <Box sx={{ marginBottom: { xs: '1px', sm: '2px', md: '3px' } }}>
            {renderSiteInfo()}
          </Box>

          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered={!isMobile}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile}
            allowScrollButtonsMobile={isMobile}
            sx={{
              backgroundColor: theme.palette.background.paper,
              padding: '6px',
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 28px rgba(0,0,0,0.45)' : '0 6px 18px rgba(16,24,40,0.10)',
              mb: 0,
              mt: 0,
              '& .MuiTabs-indicator': {
                height: '3px',
                backgroundColor: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                borderRadius: '12px',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: {
                  xs: '0.875rem',
                  sm: '0.9rem',
                  md: '1rem'
                },
                fontWeight: '500',
                color: theme.palette.text.secondary,
                padding: {
                  xs: '8px 12px',
                  sm: '10px 20px',
                  md: '12px 24px'
                },
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(10,132,255,0.06)',
                  color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                },
                '&.Mui-selected': {
                  color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  fontWeight: '600',
                },
              },
            }}
          >
            <Tab label="Table" />
            <Tab label="Visualization" />
            {isAdminOrManager && <Tab label="Logs" />}
          </Tabs>

          {selectedTab === 0 && (
            <TableTab 
              projectId={projectId} 
              siteData={siteData} 
              tagNames={tagNames} 
              loadingTags={loadingTags}
              onInstrumentAdded={refreshProjectInstruments}
            />
          )}
          {selectedTab === 1 && (
            <VisualizationTab
              projectId={projectId}
              tagNames={tagNames}
              siteData={siteData}
            />
          )}
          {isAdminOrManager && selectedTab === 2 && (
            <HistoryLogs />
          )}

          {/* Forum side drawer (right) */}
          <SwipeableDrawer
            anchor="right"
            open={openForum}
            onClose={() => setOpenForum(false)}
            onOpen={() => setOpenForum(true)}
            disableDiscovery={false}
            PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2, borderLeft: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper, top: '65px', height: 'calc(100% - 65px)' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Forum</Typography>
              <IconButton aria-label="Collapse forum" onClick={() => setOpenForum(false)} sx={{ '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand?.royalBlue || theme.palette.primary.main}`, outlineOffset: 2, borderRadius: '8px' } }}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <ForumTab projectId={projectId} />
          </SwipeableDrawer>

          <Dialog 
            open={openGatewayDialog} 
            onClose={() => setOpenGatewayDialog(false)} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
          >
            <DialogTitle sx={{ fontWeight: '600', color: (theme) => theme.palette.text.primary }}>Add Gateway</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ marginTop: '16px' }}>
                <TextField
                  fullWidth
                  label="Gateway Tag"
                  name="tag"
                  value={gatewayFormData.tag}
                  onChange={(e) => setGatewayFormData({ ...gatewayFormData, tag: e.target.value })}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Server Name"
                  name="serverName"
                  value={gatewayFormData.serverName}
                  onChange={(e) => setGatewayFormData({ ...gatewayFormData, serverName: e.target.value })}
                  variant="outlined"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenGatewayDialog(false)} color="secondary">Cancel</Button>
              <Button 
                onClick={async () => {
                  try {
                    const gatewayData = {
                      id: Date.now(),  // Unique ID for the gateway
                      tag: gatewayFormData.tag,
                      serverName: gatewayFormData.serverName,
                    };
                    await addGatewayToSite(projectId, gatewayData);  // Call the new function
                    console.log('Gateway added successfully');
                    setOpenGatewayDialog(false);
                    setGatewayFormData({ tag: '', serverName: '' });  // Reset form
                  } catch (error) {
                    console.error('Error adding gateway:', error);
                  }
                }} 
                variant="contained" 
                color="primary"
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>



          {/* Plant View Dialog */}
          {openPlantView && (
            <PlantView
              projectId={projectId}
              siteData={siteData}
              onClose={() => setOpenPlantView(false)}
            />
          )}

        </Box>
      </Container>
    </Box>
  );
};

export default IndividualProjectPage;
